import { Badge, Button, Group, Select, Text, Title } from '@mantine/core';
import { IconGitCompare } from '@tabler/icons-react';
import { useState, useMemo } from 'react';
import { api } from '~/trpc-extra/api';
import DiffViewer from '../diffViewer/DiffViewer';

export default function VersionComparison({ testId }: { testId: string }) {
  const [version1Id, setVersion1Id] = useState<string>('');
  const [version2Id, setVersion2Id] = useState<string>('');

  const { data: test, isLoading: isTestLoading } = api.test.getTest.useQuery({ id: testId });
  const { data: versionedContents, isLoading: isVersionedContentsLoading } =
    api.test.listPublishedVersionedContents.useQuery({ testId }, { enabled: !!test });

  const { data: version1Content, isLoading: isVersion1Loading } = api.testContent.get.useQuery(
    { id: version1Id },
    { enabled: !!version1Id },
  );

  const { data: version2Content, isLoading: isVersion2Loading } = api.testContent.get.useQuery(
    { id: version2Id },
    { enabled: !!version2Id },
  );

  const isLoading = isTestLoading || isVersionedContentsLoading;
  const isContentLoading = isVersion1Loading || isVersion2Loading;

  const versionOptions = useMemo(() => {
    const options = [];
    const addedIds = new Set<string>();

    if (test?.trunkContentId && !addedIds.has(test.trunkContentId)) {
      options.push({
        value: test.trunkContentId,
        label: `Current Version (Trunk) #${test.trunkContentId.slice(0, 5)}`,
      });
      addedIds.add(test.trunkContentId);
    }

    versionedContents?.forEach((version) => {
      if (!addedIds.has(version.id)) {
        options.push({
          value: version.id,
          label: `Version #${version.id.slice(0, 5)} - ${version.updatedAt.toLocaleString()}`,
        });
        addedIds.add(version.id);
      }
    });

    return options;
  }, [test?.trunkContentId, versionedContents]);

  const canCompare = version1Id && version2Id && version1Id !== version2Id && version1Content && version2Content;
  const hasSameVersionSelected = version1Id && version2Id && version1Id === version2Id;

  const resetComparison = () => {
    setVersion1Id('');
    setVersion2Id('');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Text
          size="sm"
          c="dimmed"
        >
          Loading versions...
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Title
          order={3}
          mb="sm"
        >
          Version Comparison
        </Title>
        <Text
          size="sm"
          c="dimmed"
        >
          Select two versions to compare their differences
        </Text>
      </div>

      <Group grow>
        <Select
          label="First Version"
          placeholder="Select first version"
          data={versionOptions}
          value={version1Id}
          onChange={(value) => setVersion1Id(value || '')}
          disabled={isLoading}
          leftSection={<IconGitCompare size={16} />}
          comboboxProps={{
            zIndex: 320,
            portalProps: { target: document.body },
          }}
        />
        <Select
          label="Second Version"
          placeholder="Select second version"
          data={versionOptions}
          value={version2Id}
          onChange={(value) => setVersion2Id(value || '')}
          disabled={isLoading}
          leftSection={<IconGitCompare size={16} />}
          comboboxProps={{
            zIndex: 320,
            portalProps: { target: document.body },
          }}
        />
      </Group>

      {!!hasSameVersionSelected && (
        <Badge
          color="yellow"
          variant="light"
        >
          Please select two different versions to compare
        </Badge>
      )}

      <Group>
        <Button
          variant="default"
          onClick={resetComparison}
          disabled={!version1Id && !version2Id}
        >
          Reset Selection
        </Button>
      </Group>

      {!!canCompare && (
        <div className="border-t pt-4 dark:border-zinc-700">
          <DiffViewer
            testId={testId}
            prevContent={{
              testContentId: version1Content.id,
              steps: version1Content.steps,
            }}
            newContent={{
              testContentId: version2Content.id,
              steps: version2Content.steps,
            }}
          />
        </div>
      )}

      {!!version1Id && !!version2Id && version1Id !== version2Id && !!isContentLoading && (
        <Text
          size="sm"
          c="dimmed"
        >
          Loading version content for comparison...
        </Text>
      )}
    </div>
  );
}
